import { Schema as S } from "effect";

/**
 * The one KIM mailbox address rule shared by every FrontDesk+ surface.
 *
 * A KIM address is an ordinary mail address inside the Telematik
 * infrastructure's KIM domain space. FrontDesk+ only ever *checks
 * plausibility*: the Verzeichnisdienst remains the authority on whether an
 * address exists and on who may receive what. This rule exists so a typo
 * cannot become a working Online-Check-in QR code, where the address decides
 * where an insurer sends a patient's insurance data.
 *
 * The pattern matches the gematik contracts rule
 * (`features/gematik/contracts/src/kim/kim-address.ts`) so the two surfaces
 * cannot drift on what counts as a KIM address. That rule accepts uppercase,
 * and FrontDesk+ deliberately does **not** lowercase what it was given: an
 * SMTP local part may be case-significant, so rewriting it could address a
 * different mailbox than the practice configured. Normalisation is therefore
 * whitespace only.
 *
 * @since 1.1.0
 */
export const KIM_ADDRESS_REGEX =
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.kim\.telematik(?:-test)?$/;

/**
 * Effect Schema for a plausible KIM mailbox address.
 *
 * @since 1.1.0
 * @example
 * ```ts
 * S.decodeUnknownSync(KimAddressSchema)("praxis@musterpraxis.kim.telematik")
 * ```
 */
export const KimAddressSchema = S.String.pipe(S.check(S.isPattern(KIM_ADDRESS_REGEX)));

/**
 * A syntactically valid, non-routable example used as an input placeholder.
 *
 * @since 1.1.0
 */
export const KIM_ADDRESS_PLACEHOLDER = "praxis@musterpraxis.kim.telematik";

/**
 * Normalise a typed KIM address before it is checked or stored.
 *
 * Trims surrounding whitespace and nothing else — see the module note on why
 * case is preserved.
 *
 * @since 1.1.0
 * @example
 * ```ts
 * normalizeKimAddress("  praxis@musterpraxis.kim.telematik ")
 * // "praxis@musterpraxis.kim.telematik"
 * ```
 */
export function normalizeKimAddress(value: string): string {
  return value.trim();
}

/**
 * True when `value` is a plausible KIM mailbox address after normalisation.
 *
 * @since 1.1.0
 * @example
 * ```ts
 * isPlausibleKimAddress("praxis@musterpraxis.kim.telematik") // true
 * isPlausibleKimAddress("praxis@kv-safenet.de") // false — not a KIM domain
 * ```
 */
export function isPlausibleKimAddress(value: string): boolean {
  return KIM_ADDRESS_REGEX.test(normalizeKimAddress(value));
}
